import { Link } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { featuredProductsConfig } from '../../../mocks/featuredProducts';

// Máximo 6 productos destacados
const featuredProducts = featuredProductsConfig.slice(0, 6);

export default function FeaturedProducts() {
  const { addToCart } = useCart();

  /** Formatea números de precio añadiendo puntos como separador de miles. */
  const formatPrice = (price: number) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  /** Calcula el precio del pack basado en precio unitario × unidades por pack */
  const calculatePackPrice = (unitPrice: number, unitsPerPack: number) => {
    return unitPrice * unitsPerPack;
  };

  /** Abre WhatsApp con un mensaje pre‑llenado para cotizar el producto. */
  const handleCotizar = (productName: string, unitPrice: number, unitsPerPack: number) => {
    try {
      const packPrice = calculatePackPrice(unitPrice, unitsPerPack);
      const message = `Hola, quiero cotizar el producto: ${productName} - Precio pack: $${formatPrice(
        packPrice
      )}`;
      window.open(
        `https://wa.me/56912345678?text=${encodeURIComponent(message)}`,
        '_blank'
      );
    } catch (error) {
      console.error('Error al intentar abrir WhatsApp:', error);
    }
  };

  /** Añade el producto al carrito manejando posibles errores. */
  const handleAddToCart = (product: typeof featuredProducts[0]) => {
    try {
      const packPrice = calculatePackPrice(product.unitPrice, product.unitsPerPack);
      addToCart({
        id: product.id,
        name: product.name,
        image: product.image,
        packPrice: packPrice,
        unitsPerPack: product.unitsPerPack,
      });
    } catch (error) {
      console.error('Error al agregar al carrito:', error);
    }
  };

  return (
    <section className="py-28 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-lime-500/20 text-lime-400 rounded-full text-sm font-semibold mb-4 border border-lime-500/30">
              <i className="ri-fire-fill"></i>
              Los Más Vendidos
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white">
              Productos <span className="text-lime-400">Destacados</span>
            </h2>
            <p className="text-lg text-gray-400 mt-3">
              Mix de productos de todas las categorías
            </p>
          </div>
          <Link
            to="/gaseosas"
            className="mt-6 lg:mt-0 px-6 py-3 border-2 border-lime-400 text-lime-400 rounded-lg font-semibold hover:bg-lime-400 hover:text-black transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            Ver Catálogo Completo
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Products Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-product-shop="true"
        >
          {featuredProducts.map((product, index) => {
            const packPrice = calculatePackPrice(product.unitPrice, product.unitsPerPack);
            const palletPrice = product.palletPrice ? calculatePackPrice(product.unitPrice, product.unitsPerPack) * (product.palletPrice / product.packPrice) : null;
            
            return (
              <div
                key={`featured-${product.category}-${product.id}-${index}`}
                className="bg-zinc-900 border border-lime-500/20 rounded-xl overflow-hidden hover:border-lime-400/50 hover:shadow-xl hover:shadow-lime-500/10 transition-all group"
              >
                {/* Product Image */}
                <div className="w-full h-64 bg-zinc-800 flex items-center justify-center p-6 relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    title={`${product.name} - Bebida Energética Mayorista`}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-lime-500 text-black text-xs font-bold rounded-full whitespace-nowrap">
                      {product.unitsPerPack} und/pack
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white mb-3 truncate">
                    {product.name}
                  </h3>

                  {/* Pricing */}
                  <div className="space-y-2 mb-5">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Precio unitario (ref.):</span>
                      <span className="text-gray-400">
                        ${formatPrice(product.unitPrice)}
                      </span>
                    </div>
                    <div className="bg-lime-500/15 border border-lime-500/30 rounded-lg p-3 flex justify-between items-center">
                      <span className="text-lime-400 font-semibold text-sm">
                        Precio pack:
                      </span>
                      <span className="text-lime-400 font-black text-xl">
                        ${formatPrice(packPrice)}
                      </span>
                    </div>
                    {palletPrice && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Precio pallet:</span>
                        <span className="text-lime-400 font-semibold">
                          ${formatPrice(palletPrice)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() =>
                        handleCotizar(product.name, product.unitPrice, product.unitsPerPack)
                      }
                      className="flex-1 bg-transparent border-2 border-lime-500 text-lime-400 py-2.5 rounded-lg font-semibold hover:bg-lime-500/10 transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer text-sm"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      Cotizar
                    </button>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-lime-500 text-black py-2.5 rounded-lg font-semibold hover:bg-lime-400 transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer text-sm"
                    >
                      <i className="ri-shopping-cart-line text-lg"></i>
                      Comprar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14">
          <Link
            to="/gaseosas"
            className="px-8 py-4 bg-lime-500 text-black rounded-lg font-bold text-lg hover:bg-lime-400 transition-colors inline-flex items-center gap-2 whitespace-nowrap shadow-lg shadow-lime-500/30"
          >
            <i className="ri-store-2-line text-2xl"></i>
            Ver Todo el Catálogo
          </Link>
        </div>
      </div>
    </section>
  );
}
